import React, { Fragment, ReactElement, useEffect, useRef, useState } from 'react';

// Components

import SummaryItem from '../SummaryItem';
import MaskUnmaskIcon from '../MaskUnmaskData';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { receiveSSNs } from '@actions/ssap/ssapActionCreators';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { MASK_TIMEOUT, maskValue } from '@utils/miscellaneous/mask.utils';

// Constants

import { NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';


// Types

import { Timeout } from 'react-number-format/types/types';
import { SocialSecurityCard } from '@components/ssap/SsapTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { useSelector } from 'react-redux';
import { selectIfNoSsnReasonCollectionEnabled } from '@reducers/appReducer';

interface ISsnInfo extends SocialSecurityCard {
    idx: number,
    applyingForCoverageIndicator?: boolean
}


const SsnInfo = (props: ISsnInfo): ReactElement => {
    
    const {
        idx,
        appliedForSSN,
        firstNameOnSSNCard,
        lastNameOnSSNCard,
        middleNameOnSSNCard,
        nameSameOnSSNCardIndicator,
        noSsnIssuedAttestation,
        notAppliedForSSNReason,
        socialSecurityCardHolderIndicator,
        socialSecurityNumber,
        suffixOnSSNCard,
        applyingForCoverageIndicator    
    } = props;


    const NameOnSSNCard = {
        firstName: firstNameOnSSNCard,
        lastName: lastNameOnSSNCard,
        middleName: middleNameOnSSNCard,
        suffix: suffixOnSSNCard
    };

    const getBoolean = value => !!value;
    const getNameOnSsnCard = (...args) => {
        const [
            firstName,
            lastName,
            middleName,
            suffix
        ] = args;

        return args.every(v => !v)
            ? NO_PRIOR_DATA
            : normalizeNameOfPerson({
                firstName,
                lastName,
                middleName,
                suffix
            });
    };
    const noSsnReasonCollection = useSelector(selectIfNoSsnReasonCollectionEnabled);
    const [ssnMasked, setSsnMasked] = useState(true);
    const timeoutRef = useRef<Timeout>(null);

    const getSsnValue = (ssn) => {
        if (!ssn) {
            return NO_PRIOR_DATA;
        }
        const formattedSSN = socialSecurityNumberTools.formatSSN(ssn);
        return ssnMasked ? maskValue(formattedSSN) : formattedSSN;
    };
    const getReason = (reason) => {
        return DROPDOWN_OPTIONS.REASON_FOR_NO_SSN.find(option =>
            option.value === reason
        )?.text || NO_PRIOR_DATA;
    };
        
    useEffect(() => {
        if (!ssnMasked && !timeoutRef.current) {
            // Start a timeout to reset ssnMasked to true after 15 seconds
            timeoutRef.current = setTimeout(() => {
                setSsnMasked(true);
                timeoutRef.current = null;
            }, MASK_TIMEOUT);
        }

        // Cleanup timeout if ssnMasked changes or component unmounts
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [ssnMasked]);

    return (
        <Fragment>
            <SummaryItem
                textCode = 'common.have_ssn'
                value = { !!socialSecurityNumber }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].socialSecurityCard.socialSecurityNumber`,
                            getBoolean
                        ]
                    ]
                }
            />

            {
                !!socialSecurityNumber &&

                <SummaryItem
                    textCode = 'common.ssn'
                    icon={
                        <MaskUnmaskIcon
                            receiveData={receiveSSNs}
                            masked={ssnMasked}
                            setMasked={setSsnMasked}
                        />
                    }
                    value={getSsnValue(socialSecurityNumber)}
                    ignoreIntl
                    desc={
                        [
                            [
                                `taxHousehold.householdMember[${idx}].socialSecurityCard.socialSecurityNumber`,
                                getSsnValue
                            ]
                        ]
                    }
                />
            }
            {
                noSsnReasonCollection && applyingForCoverageIndicator && noSsnIssuedAttestation &&
                <>
                    <SummaryItem
                        textCode = 'ssap.input.legend.applied_for_ssn'
                        value = { appliedForSSN }
                        noPriorDataValues = { [ null ] }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ idx }].socialSecurityCard.appliedForSSN`
                                ]
                            ]
                        }
                    />
                    { appliedForSSN === false &&
                    <SummaryItem
                        textCode = 'ssap.input.label.reason_for_no_ssn'
                        value = { getReason(notAppliedForSSNReason) }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ idx }].socialSecurityCard.notAppliedForSSNReason`,
                                    getReason
                                ]
                            ]
                        }
                    />
                    }
                </>
            }

            {
                socialSecurityCardHolderIndicator && (
                    <Fragment>
                        <SummaryItem
                            textCode = 'common.name_on_ssn_the_same'
                            value = { nameSameOnSSNCardIndicator }
                            noPriorDataValues = { [ null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].socialSecurityCard.nameSameOnSSNCardIndicator`
                                    ]
                                ]
                            }
                        />

                        {
                            !nameSameOnSSNCardIndicator &&

                            <SummaryItem
                                textCode = 'common.name_as_shown_on_ssn'
                                value = { normalizeNameOfPerson(NameOnSSNCard) }
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.firstNameOnSSNCard`,
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.lastNameOnSSNCard`,
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.middleNameOnSSNCard`,
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.suffixOnSSNCard`,
                                            ],
                                            getNameOnSsnCard
                                        ]
                                    ]
                                }
                            />

                        }
                    </Fragment>
                )
            }
        </Fragment>
    );
};

export default SsnInfo;